/**
 * Dataset Context Provider
 * Provides comprehensive dataset state management for the VRP system
 * 
 * Features:
 * - CRUD operations for datasets
 * - Dataset lifecycle management (create, copy, version, delete)
 * - Statistics management and auto-refresh
 * - Integration with scenario context for hierarchical state
 * - Data lineage tracking and validation
 * - Performance optimization with caching
 * - Error handling with rollback support
 */

import React, { 
  createContext, 
  useContext, 
  useState, 
  useEffect, 
  useCallback, 
  useMemo,
  ReactNode 
} from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useAuth } from './AuthContext';
import { useScenario } from './ScenarioContext';
import { useContextCoordinator } from '../hooks/useContextCoordinator';
import type { 
  Dataset, 
  DatasetCreate, 
  DatasetUpdate,
  DatasetWithDetails,
  DatasetStatistics,
  DatasetCopy,
  DatasetChange,
  DatasetValidation,
  Scenario
} from '../types/vrp';

// =============================================
// CONTEXT STATE INTERFACES
// =============================================

interface DatasetContextState {
  // Current State
  currentScenario: Scenario | null;
  currentDataset: Dataset | null;
  datasets: Dataset[];
  statistics: DatasetStatistics | null;
  
  // Loading States
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isCopying: boolean;
  isVersioning: boolean;
  isLoadingStatistics: boolean;
  
  // Error States
  error: string | null;
  validationErrors: Record<string, string[]> | null;
  validationResults: DatasetValidation | null;
}

interface DatasetContextActions {
  // Dataset Selection
  setDataset: (dataset: Dataset | null) => void;
  
  // CRUD Operations
  createDataset: (data: DatasetCreate) => Promise<Dataset>;
  updateDataset: (id: number, data: DatasetUpdate) => Promise<Dataset>;
  deleteDataset: (id: number) => Promise<void>;
  
  // Advanced Operations
  copyDataset: (copyData: DatasetCopy) => Promise<Dataset>;
  createDatasetVersion: (id: number, newVersion: string, description?: string) => Promise<Dataset>;
  
  // Statistics Management
  refreshStatistics: (datasetId: number) => Promise<DatasetStatistics | null>;
  getDatasetStatistics: (datasetId: number) => Promise<DatasetStatistics | null>;
  
  // Data Management
  refreshDatasets: () => Promise<void>;
  getDatasetDetails: (id: number) => Promise<DatasetWithDetails | null>;
  validateDataset: (id: number) => Promise<DatasetValidation | null>;
  
  // Utility Functions
  clearError: () => void;
  isDatasetSelected: (datasetId: number) => boolean;
  getDatasetsByScenario: (scenarioId: number) => Dataset[];
}

type DatasetContextType = DatasetContextState & DatasetContextActions;

// =============================================
// CONTEXT CREATION
// =============================================

const DatasetContext = createContext<DatasetContextType | undefined>(undefined);

// =============================================
// DATASET PROVIDER COMPONENT
// =============================================

interface DatasetProviderProps {
  children: ReactNode;
}

export const DatasetProvider: React.FC<DatasetProviderProps> = ({ children }) => {
  // =============================================
  // HOOKS AND DEPENDENCIES
  // =============================================
  
  const { user } = useAuth();
  const { currentScenario } = useScenario();
  const queryClient = useQueryClient();
  
  // Context Update Coordination System - Prevent cascade conflicts
  const { coordinateUpdate, isContextUpdating } = useContextCoordinator();
  
  // =============================================
  // LOCAL STATE
  // =============================================
  
  const [currentDataset, setCurrentDataset] = useState<Dataset | null>(null);
  const [isAutoSelecting, setIsAutoSelecting] = useState(false);
  const [statistics, setStatistics] = useState<DatasetStatistics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]> | null>(null);
  const [validationResults, setValidationResults] = useState<DatasetValidation | null>(null);
  
  // =============================================
  // REACT QUERY SETUP
  // =============================================
  
  // Query for datasets
  const {
    data: datasetsData = [],
    isLoading: isLoadingDatasets,
    error: datasetsError,
    refetch: refetchDatasets
  } = useQuery({
    queryKey: ['datasets', currentScenario?.id],
    queryFn: async () => {
      if (!currentScenario?.id) return [];
      if (import.meta.env.DEV) {
        console.log('📊 [DatasetContext] Fetching datasets for scenario:', currentScenario.id);
      }
      const response = await apiClient.getDatasets(currentScenario.id);
      // Extract actual datasets data from Edge Function response structure
      return response.data || response;
    },
    enabled: !!currentScenario?.id && !!user,
    staleTime: 3 * 60 * 1000, // 3 minutes
    retry: 2,
    refetchOnWindowFocus: true,
    onError: (error: any) => {
      console.error('❌ [DatasetContext] Failed to fetch datasets:', error);
      // Check if this is a 404 error indicating Edge Functions not deployed
      if (error.message?.includes('404') || error.message?.includes('Not Found')) {
        setError('Edge Functions not deployed. Please deploy Supabase Edge Functions.');
      } else {
        setError(error.message || 'Failed to fetch datasets');
      }
    }
  });
  
  // Query for current dataset statistics
  const {
    data: currentStatistics,
    isLoading: isLoadingStatistics,
    error: statisticsError
  } = useQuery({
    queryKey: ['dataset-statistics', currentDataset?.id],
    queryFn: async () => {
      if (!currentDataset?.id) return null;
      console.log('📈 [DatasetContext] Fetching statistics for dataset:', currentDataset.id);
      return await apiClient.getDatasetStatistics(currentDataset.id);
    },
    enabled: !!currentDataset?.id && !!user && currentDataset.id < 1000000000000, // Skip mock datasets with timestamp IDs
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 1,
    onSuccess: (stats: DatasetStatistics | null) => {
      if (stats) {
        console.log('✅ [DatasetContext] Statistics loaded:', stats.dataset_name);
        setStatistics(stats);
      }
    },
    onError: (error: any) => {
      console.error('❌ [DatasetContext] Failed to fetch statistics:', error);
    }
  });
  
  // =============================================
  // MUTATIONS
  // =============================================
  
  // Create dataset mutation
  const createDatasetMutation = useMutation({
    mutationFn: async (data: DatasetCreate): Promise<Dataset> => {
      console.log('📊 [DatasetContext] Creating dataset via API:', data);
      console.log('📊 [DatasetContext] Current scenario context:', currentScenario);
      console.log('📊 [DatasetContext] Payload being sent:', {
        name: data.name,
        description: data.description,
        scenario_id: data.scenario_id,
        version: data.version_note || '1.0.0',
        metadata: data.snapshot_metadata
      });
      
      // Validate required fields before API call
      if (!data.name || !data.scenario_id) {
        throw new Error(`Missing required fields: name=${!!data.name}, scenario_id=${!!data.scenario_id}`);
      }
      
      // Use the real API to create the dataset
      return await apiClient.createDataset({
        name: data.name,
        description: data.description,
        scenario_id: data.scenario_id,
        version: data.version_note || '1.0.0',
        metadata: data.snapshot_metadata
      });
    },
    onSuccess: (newDataset: Dataset) => {
      console.log('✅ [DatasetContext] Dataset created successfully:', newDataset.name);
      
      // Update query cache
      queryClient.setQueryData(
        ['datasets', currentScenario?.id],
        (oldData: Dataset[] = []) => {
          const safeOldData = Array.isArray(oldData) ? oldData : [];
          return [...safeOldData, newDataset];
        }
      );
      
      // Auto-select the new dataset
      setCurrentDataset(newDataset);
      
      // Clear any errors
      setError(null);
      setValidationErrors(null);
    },
    onError: (error: any) => {
      console.error('❌ [DatasetContext] Failed to create dataset:', error);
      console.error('❌ [DatasetContext] Full error object:', JSON.stringify(error, null, 2));
      console.error('❌ [DatasetContext] Error message:', error.message);
      console.error('❌ [DatasetContext] Error details:', error.details);
      
      if (error.message?.includes('validation')) {
        setValidationErrors(error.details?.field_errors || {});
      } else {
        setError(error.message || 'Failed to create dataset');
      }
    }
  });
  
  // Update dataset mutation
  const updateDatasetMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: DatasetUpdate }): Promise<Dataset> => {
      console.log('📊 [DatasetContext] Updating dataset:', id, data);
      const response = await apiClient.updateDataset(id, data);
      // Extract actual dataset data from Edge Function response structure
      return response.data || response;
    },
    onSuccess: (updatedDataset: Dataset) => {
      console.log('✅ [DatasetContext] Dataset updated successfully:', updatedDataset.name);
      
      // Update query cache
      queryClient.setQueryData(
        ['datasets', currentScenario?.id],
        (oldData: Dataset[] = []) => {
          const safeOldData = Array.isArray(oldData) ? oldData : [];
          return safeOldData.map(dataset => 
            dataset.id === updatedDataset.id ? updatedDataset : dataset
          );
        }
      );
      
      // Update current dataset if it's the one being updated
      if (currentDataset?.id === updatedDataset.id) {
        setCurrentDataset(updatedDataset);
      }
      
      // Clear any errors
      setError(null);
      setValidationErrors(null);
    },
    onError: (error: any) => {
      console.error('❌ [DatasetContext] Failed to update dataset:', error);
      
      if (error.message?.includes('validation')) {
        setValidationErrors(error.details?.field_errors || {});
      } else {
        setError(error.message || 'Failed to update dataset');
      }
    }
  });
  
  // Delete dataset mutation
  const deleteDatasetMutation = useMutation({
    mutationFn: async (datasetId: number): Promise<void> => {
      console.log('📊 [DatasetContext] Deleting dataset:', datasetId);
      await apiClient.deleteDataset(datasetId);
    },
    onSuccess: (_, deletedDatasetId: number) => {
      console.log('✅ [DatasetContext] Dataset deleted successfully:', deletedDatasetId);
      
      // Update query cache
      queryClient.setQueryData(
        ['datasets', currentScenario?.id],
        (oldData: Dataset[] = []) => {
          const safeOldData = Array.isArray(oldData) ? oldData : [];
          return safeOldData.filter(dataset => dataset.id !== deletedDatasetId);
        }
      );
      
      // Clear current dataset if it was deleted
      if (currentDataset?.id === deletedDatasetId) {
        setCurrentDataset(null);
        setStatistics(null);
      }
      
      // Clear any errors
      setError(null);
      setValidationErrors(null);
    },
    onError: (error: any) => {
      console.error('❌ [DatasetContext] Failed to delete dataset:', error);
      setError(error.message || 'Failed to delete dataset');
    }
  });
  
  // Copy dataset mutation
  const copyDatasetMutation = useMutation({
    mutationFn: async (copyData: DatasetCopy): Promise<Dataset> => {
      console.log('📊 [DatasetContext] Copying dataset:', copyData);
      return await apiClient.copyDataset(
        copyData.source_dataset_id,
        copyData.target_scenario_id,
        copyData.new_dataset_name,
        copyData.description
      );
    },
    onSuccess: (copiedDataset: Dataset) => {
      console.log('✅ [DatasetContext] Dataset copied successfully:', copiedDataset.name);
      
      // If copied to current scenario, update cache
      if (copiedDataset.scenario_id === currentScenario?.id) {
        queryClient.setQueryData(
          ['datasets', currentScenario?.id],
          (oldData: Dataset[] = []) => {
            const safeOldData = Array.isArray(oldData) ? oldData : [];
            return [...safeOldData, copiedDataset];
          }
        );
      }
      
      // Clear any errors
      setError(null);
      setValidationErrors(null);
    },
    onError: (error: any) => {
      console.error('❌ [DatasetContext] Failed to copy dataset:', error);
      setError(error.message || 'Failed to copy dataset');
    }
  });
  
  // Create dataset version mutation
  const createVersionMutation = useMutation({
    mutationFn: async ({ datasetId, newVersion, description }: { 
      datasetId: number; 
      newVersion: string; 
      description?: string; 
    }): Promise<Dataset> => {
      console.log('📊 [DatasetContext] Creating dataset version:', datasetId, newVersion);
      return await apiClient.createDatasetVersion(datasetId, newVersion, description);
    },
    onSuccess: (versionedDataset: Dataset) => {
      console.log('✅ [DatasetContext] Dataset version created successfully:', versionedDataset.name);
      
      // Update query cache
      queryClient.setQueryData(
        ['datasets', currentScenario?.id],
        (oldData: Dataset[] = []) => {
          const safeOldData = Array.isArray(oldData) ? oldData : [];
          return [...safeOldData, versionedDataset];
        }
      );
      
      // Clear any errors
      setError(null);
      setValidationErrors(null);
    },
    onError: (error: any) => {
      console.error('❌ [DatasetContext] Failed to create dataset version:', error);
      setError(error.message || 'Failed to create dataset version');
    }
  });
  
  // Refresh statistics mutation
  const refreshStatisticsMutation = useMutation({
    mutationFn: async (datasetId: number): Promise<DatasetStatistics> => {
      console.log('📊 [DatasetContext] Refreshing statistics for dataset:', datasetId);
      return await apiClient.refreshDatasetStatistics(datasetId);
    },
    onSuccess: (refreshedStats: DatasetStatistics) => {
      console.log('✅ [DatasetContext] Statistics refreshed successfully:', refreshedStats.dataset_name);
      
      // Update query cache
      queryClient.setQueryData(['dataset-statistics', refreshedStats.dataset_id], refreshedStats);
      
      // Update local state if it's for current dataset
      if (currentDataset?.id === refreshedStats.dataset_id) {
        setStatistics(refreshedStats);
      }
    },
    onError: (error: any) => {
      console.error('❌ [DatasetContext] Failed to refresh statistics:', error);
      setError(error.message || 'Failed to refresh statistics');
    }
  });
  
  // =============================================
  // ACTION FUNCTIONS - COORDINATED
  // =============================================
  
  const setDataset = useCallback((dataset: Dataset | null) => {
    // Skip if context is already updating to prevent cascade
    if (isContextUpdating('DatasetContext') || isAutoSelecting) {
      console.log('🔒 [DatasetContext] Skipping dataset update - context already updating or auto-selecting');
      return;
    }

    coordinateUpdate('DatasetContext', async () => {
      console.log('📊 [DatasetContext] Setting current dataset:', dataset?.name || 'none');
      setCurrentDataset(dataset);
      
      // Clear statistics when changing datasets
      if (!dataset || (currentDataset && dataset.id !== currentDataset.id)) {
        setStatistics(null);
      }
      
      // Coordinate localStorage writes to prevent conflicts
      await coordinateUpdate('LocalStorage', async () => {
        if (dataset && currentScenario?.id) {
          localStorage.setItem(
            `vrp-current-dataset-${currentScenario.id}`,
            JSON.stringify({
              id: dataset.id,
              name: dataset.name,
              scenario_id: dataset.scenario_id
            })
          );
        } else if (currentScenario?.id) {
          localStorage.removeItem(`vrp-current-dataset-${currentScenario.id}`);
        }
      }, 10); // Low priority for localStorage operations
    }, 3); // Lower priority for dataset updates
  }, [currentScenario?.id, currentDataset, coordinateUpdate, isContextUpdating, isAutoSelecting]);
  
  const createDataset = useCallback(async (data: DatasetCreate): Promise<Dataset> => {
    if (!currentScenario?.id) {
      throw new Error('No scenario selected');
    }
    
    // Check for existing dataset with same name/version combination
    // Database constraint is on (project_id, name, version)
    const existingDatasets = Array.isArray(datasetsData) ? datasetsData : [];
    const proposedVersion = data.version_note || '1.0.0';
    
    const duplicateEntry = existingDatasets.find(
      dataset => dataset.scenario_id === currentScenario.id && 
                 dataset.name.toLowerCase() === data.name.toLowerCase() &&
                 dataset.version === proposedVersion
    );
    
    if (duplicateEntry) {
      // Generate a unique version number instead of changing name
      let versionCounter = 1;
      const baseVersion = proposedVersion.split('.')[0]; // Get major version (e.g., "1" from "1.0.0")
      let uniqueVersion = `${baseVersion}.0.${versionCounter}`;
      
      while (existingDatasets.find(
        dataset => dataset.scenario_id === currentScenario.id && 
                   dataset.name.toLowerCase() === data.name.toLowerCase() &&
                   dataset.version === uniqueVersion
      )) {
        versionCounter++;
        uniqueVersion = `${baseVersion}.0.${versionCounter}`;
      }
      
      console.log(`📊 [DatasetContext] Dataset "${data.name}" version "${proposedVersion}" already exists, using version "${uniqueVersion}"`);
      
      const datasetData = {
        ...data,
        scenario_id: currentScenario.id,
        version_note: uniqueVersion
      };
      
      return createDatasetMutation.mutateAsync(datasetData);
    }
    
    const datasetData = {
      ...data,
      scenario_id: currentScenario.id
    };
    
    return createDatasetMutation.mutateAsync(datasetData);
  }, [currentScenario?.id, createDatasetMutation, datasetsData]);
  
  const updateDataset = useCallback(async (id: number, data: DatasetUpdate): Promise<Dataset> => {
    return updateDatasetMutation.mutateAsync({ id, data });
  }, [updateDatasetMutation]);
  
  const deleteDataset = useCallback(async (id: number): Promise<void> => {
    await deleteDatasetMutation.mutateAsync(id);
  }, [deleteDatasetMutation]);
  
  const copyDataset = useCallback(async (copyData: DatasetCopy): Promise<Dataset> => {
    return copyDatasetMutation.mutateAsync(copyData);
  }, [copyDatasetMutation]);
  
  const createDatasetVersion = useCallback(async (
    id: number, 
    newVersion: string, 
    description?: string
  ): Promise<Dataset> => {
    return createVersionMutation.mutateAsync({ datasetId: id, newVersion, description });
  }, [createVersionMutation]);
  
  const refreshStatistics = useCallback(async (datasetId: number): Promise<DatasetStatistics | null> => {
    try {
      return await refreshStatisticsMutation.mutateAsync(datasetId);
    } catch (error) {
      console.error('❌ [DatasetContext] Failed to refresh statistics:', error);
      return null;
    }
  }, [refreshStatisticsMutation]);
  
  const getDatasetStatistics = useCallback(async (datasetId: number): Promise<DatasetStatistics | null> => {
    try {
      console.log('📊 [DatasetContext] Fetching statistics for dataset:', datasetId);
      return await apiClient.getDatasetStatistics(datasetId);
    } catch (error: any) {
      console.error('❌ [DatasetContext] Failed to get dataset statistics:', error);
      setError(error.message || 'Failed to get dataset statistics');
      return null;
    }
  }, []);
  
  const refreshDatasets = useCallback(async (): Promise<void> => {
    console.log('📊 [DatasetContext] Manually refreshing datasets');
    await refetchDatasets();
  }, [refetchDatasets]);
  
  const getDatasetDetails = useCallback(async (id: number): Promise<DatasetWithDetails | null> => {
    try {
      console.log('📊 [DatasetContext] Fetching dataset details:', id);
      const dataset = await apiClient.getDataset(id);
      return dataset as DatasetWithDetails;
    } catch (error: any) {
      console.error('❌ [DatasetContext] Failed to get dataset details:', error);
      setError(error.message || 'Failed to get dataset details');
      return null;
    }
  }, []);
  
  const validateDataset = useCallback(async (id: number): Promise<DatasetValidation | null> => {
    try {
      console.log('📊 [DatasetContext] Validating dataset:', id);
      
      // Since there's no specific validation endpoint in the API, 
      // we'll create a basic validation based on statistics
      const stats = await getDatasetStatistics(id);
      if (!stats) return null;
      
      const validation: DatasetValidation = {
        dataset_id: id,
        is_valid: stats.vehicle_count > 0 && stats.job_count > 0,
        warnings: [],
        errors: [],
        statistics: stats
      };
      
      // Add warnings based on statistics
      if (stats.vehicle_count === 0) {
        validation.errors.push({
          type: 'critical_missing_data',
          message: 'Dataset has no vehicles',
          blocking: true
        });
      }
      
      if (stats.job_count === 0) {
        validation.errors.push({
          type: 'critical_missing_data',
          message: 'Dataset has no jobs',
          blocking: true
        });
      }
      
      if (stats.location_count === 0) {
        validation.warnings.push({
          type: 'missing_data',
          message: 'Dataset has no locations',
          affected_entities: []
        });
      }
      
      setValidationResults(validation);
      return validation;
      
    } catch (error: any) {
      console.error('❌ [DatasetContext] Failed to validate dataset:', error);
      setError(error.message || 'Failed to validate dataset');
      return null;
    }
  }, [getDatasetStatistics]);
  
  const clearError = useCallback(() => {
    setError(null);
    setValidationErrors(null);
    setValidationResults(null);
  }, []);
  
  const isDatasetSelected = useCallback((datasetId: number): boolean => {
    return currentDataset?.id === datasetId;
  }, [currentDataset?.id]);
  
  const getDatasetsByScenario = useCallback((scenarioId: number): Dataset[] => {
    const safeDatasets = Array.isArray(datasetsData) ? datasetsData : [];
    return safeDatasets.filter(dataset => dataset.scenario_id === scenarioId);
  }, [datasetsData]);
  
  // =============================================
  // COMPUTED VALUES
  // =============================================
  
  // Ensure datasets is always an array
  const datasets = useMemo(() => {
    return Array.isArray(datasetsData) ? datasetsData : [];
  }, [datasetsData]);

  // =============================================
  // AUTO-SELECTION LOGIC - COORDINATED
  // =============================================
  
  // =============================================
  // CASCADE PREVENTION LOGIC
  // =============================================

  // Auto-select dataset when scenario changes or datasets load (FIXED to prevent cascade loops)
  useEffect(() => {
    if (!currentScenario?.id || datasets.length === 0 || isAutoSelecting || isContextUpdating('DatasetContext')) {
      if (!currentScenario?.id || datasets.length === 0) {
        setCurrentDataset(null);
        setStatistics(null);
      }
      return; // Prevent cascade during auto-selection
    }
    
    setIsAutoSelecting(true);
    
    // Debounced auto-selection logic
    const timeoutId = setTimeout(() => {
      coordinateUpdate('DatasetContext', async () => {
        try {
          // Try to restore from localStorage
          const savedDatasetKey = `vrp-current-dataset-${currentScenario.id}`;
          const savedDatasetData = localStorage.getItem(savedDatasetKey);
          
          if (savedDatasetData) {
            try {
              const savedDataset = JSON.parse(savedDatasetData);
              const foundDataset = datasets.find(d => d.id === savedDataset.id);
              
              if (foundDataset) {
                if (import.meta.env.DEV) {
                  console.log('📊 [DatasetContext] Restoring saved dataset:', foundDataset.name);
                }
                setCurrentDataset(foundDataset);
                return;
              } else {
                if (import.meta.env.DEV) {
                  console.log('⚠️ [DatasetContext] Saved dataset not found, clearing localStorage');
                }
                localStorage.removeItem(savedDatasetKey);
              }
            } catch (error) {
              if (import.meta.env.DEV) {
                console.warn('⚠️ [DatasetContext] Failed to parse saved dataset data:', error);
              }
              localStorage.removeItem(savedDatasetKey);
            }
          }
          
          // If no saved dataset or it's not available, auto-select first active dataset
          const activeDatasets = datasets.filter(d => d.status === 'active');
          if (activeDatasets.length > 0) {
            if (import.meta.env.DEV) {
              console.log('📊 [DatasetContext] Auto-selecting first active dataset:', activeDatasets[0].name);
            }
            setCurrentDataset(activeDatasets[0]);
          } else if (datasets.length > 0) {
            if (import.meta.env.DEV) {
              console.log('📊 [DatasetContext] Auto-selecting first dataset:', datasets[0].name);
            }
            setCurrentDataset(datasets[0]);
          }
        } finally {
          setIsAutoSelecting(false);
        }
      }, 4); // Lowest priority for dataset auto-selection
    }, 300); // Slightly longer debounce for dataset selection
    
    return () => {
      clearTimeout(timeoutId);
      setIsAutoSelecting(false);
    };
  }, [currentScenario?.id]); // Remove datasets from dependencies to prevent cascade
  
  // Clear current dataset when scenario changes
  useEffect(() => {
    if (!currentScenario?.id) {
      setCurrentDataset(null);
      setStatistics(null);
      setError(null);
      setValidationErrors(null);
      setValidationResults(null);
    }
  }, [currentScenario?.id]);
  
  // =============================================
  // ERROR HANDLING
  // =============================================
  
  // Handle query errors
  useEffect(() => {
    if (datasetsError) {
      const errorMessage = datasetsError instanceof Error ? datasetsError.message : 'Failed to fetch datasets';
      console.error('❌ [DatasetContext] Query error:', errorMessage);
      setError(errorMessage);
    }
  }, [datasetsError]);
  
  useEffect(() => {
    if (statisticsError) {
      const errorMessage = statisticsError instanceof Error ? statisticsError.message : 'Failed to fetch statistics';
      console.error('❌ [DatasetContext] Statistics error:', errorMessage);
    }
  }, [statisticsError]);
  
  // =============================================
  // COMPUTED STATE
  // =============================================
  
  const contextValue = useMemo<DatasetContextType>(() => ({
    // State
    currentScenario,
    currentDataset,
    datasets,
    statistics: statistics || currentStatistics,
    
    // Loading States
    isLoading: isLoadingDatasets,
    isCreating: createDatasetMutation.isPending,
    isUpdating: updateDatasetMutation.isPending,
    isDeleting: deleteDatasetMutation.isPending,
    isCopying: copyDatasetMutation.isPending,
    isVersioning: createVersionMutation.isPending,
    isLoadingStatistics: isLoadingStatistics || refreshStatisticsMutation.isPending,
    
    // Error States
    error,
    validationErrors,
    validationResults,
    
    // Actions
    setDataset,
    createDataset,
    updateDataset,
    deleteDataset,
    copyDataset,
    createDatasetVersion,
    refreshStatistics,
    getDatasetStatistics,
    refreshDatasets,
    getDatasetDetails,
    validateDataset,
    clearError,
    isDatasetSelected,
    getDatasetsByScenario
  }), [
    currentScenario,
    currentDataset,
    datasets,
    statistics,
    currentStatistics,
    isLoadingDatasets,
    createDatasetMutation.isPending,
    updateDatasetMutation.isPending,
    deleteDatasetMutation.isPending,
    copyDatasetMutation.isPending,
    createVersionMutation.isPending,
    isLoadingStatistics,
    refreshStatisticsMutation.isPending,
    error,
    validationErrors,
    validationResults,
    setDataset,
    createDataset,
    updateDataset,
    deleteDataset,
    copyDataset,
    createDatasetVersion,
    refreshStatistics,
    getDatasetStatistics,
    refreshDatasets,
    getDatasetDetails,
    validateDataset,
    clearError,
    isDatasetSelected,
    getDatasetsByScenario
  ]);
  
  // =============================================
  // DEBUG LOGGING
  // =============================================
  
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('📊 [DatasetContext] State update:', {
        currentScenario: currentScenario?.name || 'none',
        currentDataset: currentDataset?.name || 'none',
        datasetsCount: datasets.length,
        hasStatistics: !!statistics,
        isLoading: isLoadingDatasets,
        error: error || 'none'
      });
    }
  }, [currentScenario, currentDataset, datasets, statistics, isLoadingDatasets, error]);
  
  // =============================================
  // RENDER
  // =============================================
  
  return (
    <DatasetContext.Provider value={contextValue}>
      {children}
    </DatasetContext.Provider>
  );
};

// =============================================
// CUSTOM HOOK
// =============================================

export const useDataset = (): DatasetContextType => {
  const context = useContext(DatasetContext);
  
  if (context === undefined) {
    throw new Error('useDataset must be used within a DatasetProvider');
  }
  
  return context;
};

// =============================================
// EXPORT DEFAULT
// =============================================

export default DatasetProvider;